import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, Check, ClipboardCheck, Minus, Pencil, Printer, Trash2, Upload, Wrench, X } from 'lucide-react';
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { DocumentList } from '../../components/DocumentList';
import { DocumentUploadModal } from '../../components/DocumentUploadModal';
import { AssigneeSelect, EquipmentSelect, InspectionStatusBadge, PlaceFields, ScoreBadge } from '../../components/facilities-ui';
import { useToast } from '../../components/toast';
import { WorkOrderStatusBadge } from '../../components/work-order-badges';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  cn,
  ConfirmDialog,
  DescriptionList,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Modal,
  PageHeader,
  PageLoader,
  Textarea,
} from '../../components/ui';
import { api, ApiError, errorMessage } from '../../lib/api';
import { useDesk } from '../../lib/desk';
import {
  equipmentLabel,
  INSPECTION_KIND_LABEL,
  INSPECTION_RESULT_LABEL,
  placeLabel,
  type InspectionDetail,
  type InspectionDetailItem,
  type InspectionResult,
} from '../../lib/facilities';
import { formatDateTime, formatUtcDate } from '../../lib/format';

type ResponseInput = { itemId: string; result: InspectionResult | null; note?: string | null };

const RESULTS: InspectionResult[] = ['PASS', 'FAIL', 'NOT_APPLICABLE'];
const RESULT_ICON: Record<InspectionResult, ReactNode> = { PASS: <Check />, FAIL: <X />, NOT_APPLICABLE: <Minus /> };
const RESULT_SELECTED: Record<InspectionResult, string> = {
  PASS: 'border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-700',
  FAIL: 'border-red-600 bg-red-600 text-white hover:bg-red-700',
  NOT_APPLICABLE: 'border-slate-600 bg-slate-600 text-white hover:bg-slate-700',
};
const RESULT_TONE = { PASS: 'green', FAIL: 'red', NOT_APPLICABLE: 'gray' } as const;

const plural = (count: number, word: string) => `${count.toLocaleString()} ${word}${count === 1 ? '' : 's'}`;

/** Item ids the server lists when an inspection can't be completed yet. */
function missingItemIds(error: unknown): string[] {
  if (!(error instanceof ApiError) || !error.details || typeof error.details !== 'object') return [];
  const { itemIds } = error.details as { itemIds?: unknown };
  return Array.isArray(itemIds) ? itemIds.filter((itemId): itemId is string => typeof itemId === 'string') : [];
}

/** Items grouped by section, in the order each section first appears. */
function groupBySection(items: InspectionDetailItem[]) {
  const groups = new Map<string, InspectionDetailItem[]>();
  for (const item of items) {
    const group = groups.get(item.section);
    if (group) group.push(item);
    else groups.set(item.section, [item]);
  }
  return [...groups.entries()].map(([section, sectionItems]) => ({ section, items: sectionItems }));
}

function countResults(items: InspectionDetailItem[]) {
  const count = (result: InspectionResult) => items.filter((item) => item.result === result).length;
  const passed = count('PASS');
  const failed = count('FAIL');
  const notApplicable = count('NOT_APPLICABLE');
  return { passed, failed, notApplicable, answered: passed + failed + notApplicable, total: items.length };
}

// ---------------------------------------------------------------------------
// Checklist
// ---------------------------------------------------------------------------

function ResultBadge({ result }: { result: InspectionResult | null }) {
  return result ? <Badge tone={RESULT_TONE[result]}>{INSPECTION_RESULT_LABEL[result]}</Badge> : <span className="text-xs text-slate-400">Not marked</span>;
}

function ChecklistItem({
  item,
  canRespond,
  highlighted,
  onSave,
}: {
  item: InspectionDetailItem;
  canRespond: boolean;
  highlighted: boolean;
  onSave(response: ResponseInput): void;
}) {
  const [note, setNote] = useState(item.note ?? '');
  const noteRef = useRef<HTMLTextAreaElement>(null);

  // Take the saved note, unless the inspector is typing in it.
  useEffect(() => {
    if (document.activeElement !== noteRef.current) setNote(item.note ?? '');
  }, [item.note]);

  const saveNote = () => {
    const next = note.trim();
    if (next === (item.note ?? '')) return;
    onSave({ itemId: item.id, result: item.result, note: next || null });
  };
  const showNote = item.result === 'FAIL' || !!item.note || note !== '';

  return (
    <li id={`inspection-item-${item.id}`} className={cn('px-5 py-3 break-inside-avoid', highlighted && 'bg-red-50/60 ring-2 ring-red-500 ring-inset')}>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-900">{item.label}</p>
          {item.guidance && <p className="mt-0.5 text-xs text-slate-500">{item.guidance}</p>}
          {highlighted && <p className="mt-1 text-xs font-medium text-red-700 print:hidden">Mark this item pass, fail, or not applicable</p>}
        </div>
        {canRespond ? (
          <>
            <div role="group" aria-label={`Result for ${item.label}`} className="flex shrink-0 flex-wrap gap-1.5 print:hidden">
              {RESULTS.map((result) => {
                const selected = item.result === result;
                return (
                  <button
                    key={result}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => onSave({ itemId: item.id, result: selected ? null : result })}
                    className={cn(
                      'inline-flex h-8 items-center gap-1.5 rounded-lg border px-2.5 text-xs font-medium shadow-sm transition-colors [&_svg]:size-3.5',
                      selected ? RESULT_SELECTED[result] : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50',
                    )}
                  >
                    {RESULT_ICON[result]}
                    {INSPECTION_RESULT_LABEL[result]}
                  </button>
                );
              })}
            </div>
            <div className="hidden shrink-0 print:block">
              <ResultBadge result={item.result} />
            </div>
          </>
        ) : (
          <div className="shrink-0">
            <ResultBadge result={item.result} />
          </div>
        )}
      </div>

      {canRespond && showNote && (
        <>
          <Textarea
            ref={noteRef}
            rows={2}
            maxLength={2000}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onBlur={saveNote}
            placeholder={item.result === 'FAIL' ? 'What’s wrong, and where? The follow-up work order includes this note.' : 'Note'}
            aria-label={`Note for ${item.label}`}
            className="mt-2 print:hidden"
          />
          {item.note && <p className="mt-2 hidden text-sm whitespace-pre-wrap text-slate-700 print:block">{item.note}</p>}
        </>
      )}
      {!canRespond && item.note && <p className="mt-2 text-sm whitespace-pre-wrap text-slate-700">{item.note}</p>}

      {item.workOrder && (
        <p className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-600">
          <Wrench className="size-3.5 text-slate-400" />
          Follow-up
          <Link to={`/facilities/work-orders/${item.workOrder.id}`} className="font-medium text-brand-700 hover:underline">
            #{item.workOrder.number} {item.workOrder.title}
          </Link>
          <WorkOrderStatusBadge status={item.workOrder.status} />
        </p>
      )}
    </li>
  );
}

function ChecklistProgress({ items }: { items: InspectionDetailItem[] }) {
  const { passed, failed, notApplicable, answered, total } = countResults(items);
  const width = (count: number) => `${total ? (count * 100) / total : 0}%`;
  return (
    <div className="border-b border-slate-100 px-5 py-3">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <p className="text-sm font-medium text-slate-800">
          {answered.toLocaleString()} of {plural(total, 'item')}
        </p>
        <p className="text-xs text-slate-500">
          <span className="font-medium text-emerald-700">{passed.toLocaleString()} passed</span>
          {' · '}
          <span className={cn('font-medium', failed > 0 ? 'text-red-700' : 'text-slate-500')}>{failed.toLocaleString()} failed</span>
          {' · '}
          {notApplicable.toLocaleString()} not applicable
        </p>
      </div>
      <div
        className="mt-2 flex h-2 overflow-hidden rounded-full bg-slate-100"
        role="progressbar"
        aria-label="Items marked"
        aria-valuenow={answered}
        aria-valuemin={0}
        aria-valuemax={total}
      >
        <div className="bg-emerald-500" style={{ width: width(passed) }} />
        <div className="bg-red-500" style={{ width: width(failed) }} />
        <div className="bg-slate-400" style={{ width: width(notApplicable) }} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modals
// ---------------------------------------------------------------------------

const editForm = (inspection: InspectionDetail) => ({
  name: inspection.name,
  buildingId: inspection.buildingId ?? '',
  roomId: inspection.roomId ?? '',
  facilityAssetId: inspection.facilityAssetId ?? '',
  inspectorId: inspection.inspectorId ?? '',
  dueOn: inspection.dueOn ?? '',
  notes: inspection.notes ?? '',
});

function EditInspectionModal({ inspection, open, onClose }: { inspection: InspectionDetail; open: boolean; onClose(): void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(() => editForm(inspection));
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setForm(editForm(inspection));
    setError('');
    // Only when it opens: the inspection refreshes in the background while the form is open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const save = useMutation({
    mutationFn: () =>
      api.patch<InspectionDetail>(`/facilities/inspections/${inspection.id}`, {
        name: form.name.trim(),
        buildingId: form.buildingId || null,
        roomId: form.roomId || null,
        facilityAssetId: form.facilityAssetId || null,
        inspectorId: form.inspectorId || null,
        dueOn: form.dueOn || null,
        notes: form.notes.trim() || null,
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(['facilities', 'inspection', inspection.id], data);
      void queryClient.invalidateQueries({ queryKey: ['facilities'] });
      toast.success('Inspection saved');
      onClose();
    },
    onError: (err) => setError(errorMessage(err)),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError('');
    save.mutate();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit inspection details"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" form="edit-inspection-form" loading={save.isPending}>
            Save
          </Button>
        </>
      }
    >
      <form id="edit-inspection-form" onSubmit={submit} className="space-y-4">
        {error && <Alert tone="red">{error}</Alert>}
        <Field label="Name" required>
          <Input value={form.name} required maxLength={150} autoFocus onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <PlaceFields
            buildingId={form.buildingId}
            roomId={form.roomId}
            onChange={(place) => setForm({ ...form, ...place, facilityAssetId: place.buildingId === form.buildingId ? form.facilityAssetId : '' })}
          />
        </div>
        <Field label="Equipment">
          <EquipmentSelect value={form.facilityAssetId} buildingId={form.buildingId || undefined} onChange={(facilityAssetId) => setForm({ ...form, facilityAssetId })} />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Inspector">
            <AssigneeSelect value={form.inspectorId} emptyLabel="Nobody yet" onChange={(inspectorId) => setForm({ ...form, inspectorId })} />
          </Field>
          <Field label="Due">
            <Input type="date" value={form.dueOn} onChange={(e) => setForm({ ...form, dueOn: e.target.value })} />
          </Field>
        </div>
        <Field label="Notes">
          <Textarea rows={3} maxLength={5000} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </Field>
      </form>
    </Modal>
  );
}

function CompleteInspectionModal({
  inspection,
  open,
  onClose,
  onMissing,
}: {
  inspection: InspectionDetail;
  open: boolean;
  onClose(): void;
  onMissing(itemIds: string[]): void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setNotes(inspection.notes ?? '');
    setError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const complete = useMutation({
    mutationFn: () => api.post<InspectionDetail>(`/facilities/inspections/${inspection.id}/complete`, { notes: notes.trim() || null }),
    onSuccess: (data) => {
      queryClient.setQueryData(['facilities', 'inspection', inspection.id], data);
      void queryClient.invalidateQueries({ queryKey: ['facilities'] });
      void queryClient.invalidateQueries({ queryKey: ['work-orders'] });
      const opened = data.followUps.length;
      toast.success(`Inspection completed${opened ? ` and opened ${plural(opened, 'follow-up work order')}` : ''}`);
      onClose();
    },
    onError: (err) => {
      const itemIds = missingItemIds(err);
      if (itemIds.length === 0) {
        setError(errorMessage(err));
        return;
      }
      toast.error(errorMessage(err));
      onClose();
      onMissing(itemIds);
    },
  });

  const { passed, failed, notApplicable, answered, total } = countResults(inspection.items);
  const unanswered = total - answered;
  const newFollowUps = inspection.items.filter((item) => item.result === 'FAIL' && !item.workOrder).length;
  const followUpsOn = inspection.template?.createFollowUps ?? true;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError('');
    complete.mutate();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Complete inspection"
      description={`${passed.toLocaleString()} passed · ${failed.toLocaleString()} failed · ${notApplicable.toLocaleString()} not applicable`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" form="complete-inspection-form" icon={<ClipboardCheck />} loading={complete.isPending}>
            Complete inspection
          </Button>
        </>
      }
    >
      <form id="complete-inspection-form" onSubmit={submit} className="space-y-4">
        {error && <Alert tone="red">{error}</Alert>}
        {unanswered > 0 && (
          <Alert tone="amber">
            {plural(unanswered, 'item')} {unanswered === 1 ? 'isn’t' : 'aren’t'} marked yet. Every item needs pass, fail, or not applicable.
          </Alert>
        )}
        <p className="text-sm text-slate-600">
          {!followUpsOn
            ? 'Completing it scores the inspection. This template doesn’t open follow-up work orders.'
            : newFollowUps > 0
              ? `Completing it scores the inspection and opens ${plural(newFollowUps, 'follow-up work order')} for the failed items.`
              : 'Completing it scores the inspection. Nothing failed, so no follow-up work orders are opened.'}{' '}
          Results can’t be changed afterwards.
        </p>
        <Field label="Notes" hint="Saved with the inspection record">
          <Textarea rows={4} maxLength={5000} value={notes} placeholder="Anything else the report should mention" onChange={(e) => setNotes(e.target.value)} />
        </Field>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function InspectionDetailPage() {
  const { id = '' } = useParams();
  const desk = useDesk();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const queryKey = ['facilities', 'inspection', id];

  const query = useQuery({
    queryKey,
    queryFn: () => api.get<InspectionDetail>(`/facilities/inspections/${id}`),
    refetchInterval: (current) => (current.state.data?.status === 'SCHEDULED' || current.state.data?.status === 'IN_PROGRESS' ? 30_000 : false),
  });

  const [editing, setEditing] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [missing, setMissing] = useState<Set<string>>(() => new Set());
  const [scrollTarget, setScrollTarget] = useState<string | null>(null);

  // After the complete modal closes, bring the first unmarked item into view.
  useEffect(() => {
    if (!scrollTarget) return;
    document.getElementById(`inspection-item-${scrollTarget}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setScrollTarget(null);
  }, [scrollTarget]);

  // Results save as they're clicked and show right away. A response only holds the clicks the server had seen, so
  // when saves overlap the cache keeps the clicked results and refreshes once they've all finished.
  const pendingSaves = useRef(0);
  const overlapped = useRef(false);
  const respond = useMutation({
    mutationFn: (response: ResponseInput) => api.put<InspectionDetail>(`/facilities/inspections/${id}/responses`, { responses: [response] }),
    onMutate: async (response) => {
      pendingSaves.current += 1;
      if (pendingSaves.current > 1) overlapped.current = true;
      if (response.result) {
        setMissing((current) => {
          if (!current.has(response.itemId)) return current;
          const next = new Set(current);
          next.delete(response.itemId);
          return next;
        });
      }
      await queryClient.cancelQueries({ queryKey });
      queryClient.setQueryData<InspectionDetail>(
        queryKey,
        (current) =>
          current && {
            ...current,
            items: current.items.map((item) =>
              item.id === response.itemId ? { ...item, result: response.result, ...(response.note !== undefined ? { note: response.note } : {}) } : item,
            ),
          },
      );
    },
    onSuccess: (data) => {
      if (!overlapped.current) queryClient.setQueryData(queryKey, data);
    },
    onError: (err) => {
      overlapped.current = true;
      toast.error(errorMessage(err));
    },
    onSettled: () => {
      pendingSaves.current -= 1;
      if (pendingSaves.current === 0 && overlapped.current) {
        overlapped.current = false;
        void queryClient.invalidateQueries({ queryKey });
      }
    },
  });

  const cancel = useMutation({
    mutationFn: () => api.post<InspectionDetail>(`/facilities/inspections/${id}/cancel`),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKey, data);
      void queryClient.invalidateQueries({ queryKey: ['facilities'] });
      setCanceling(false);
      toast.success('Inspection canceled');
    },
    onError: (err) => {
      setCanceling(false);
      toast.error(errorMessage(err));
    },
  });

  const remove = useMutation({
    mutationFn: () => api.delete(`/facilities/inspections/${id}`),
    onSuccess: () => {
      toast.success('Inspection deleted');
      navigate('/facilities/inspections', { replace: true });
      queryClient.removeQueries({ queryKey, exact: true });
      void queryClient.invalidateQueries({ queryKey: ['facilities'] });
    },
    onError: (err) => {
      setDeleting(false);
      toast.error(errorMessage(err));
    },
  });

  if (!query.data) return query.error ? <ErrorState error={query.error} onRetry={() => query.refetch()} /> : <PageLoader />;

  const inspection = query.data;
  const { permissions } = inspection;
  const completed = inspection.status === 'COMPLETED';
  const groups = groupBySection(inspection.items);
  const hasSections = groups.some((group) => group.section);
  const unanswered = inspection.items.filter((item) => !item.result).length;
  const followUpsOn = inspection.template?.createFollowUps ?? true;
  const where = [placeLabel(inspection), inspection.facilityAsset && equipmentLabel(inspection.facilityAsset)].filter(Boolean).join(' · ');

  const details: { label: string; value: ReactNode }[] = [
    { label: 'Building and room', value: placeLabel(inspection) || null },
    {
      label: 'Equipment',
      value: inspection.facilityAsset ? (
        <Link to={`/facilities/equipment/${inspection.facilityAsset.id}`} className="text-brand-700 hover:underline">
          {equipmentLabel(inspection.facilityAsset)}
        </Link>
      ) : null,
    },
    { label: 'Inspector', value: inspection.inspector?.name ?? null },
    {
      label: 'Due',
      value: inspection.dueOn ? (
        <span className={inspection.overdue ? 'font-medium text-red-600' : undefined}>
          {formatUtcDate(inspection.dueOn)}
          {inspection.overdue && ' · overdue'}
        </span>
      ) : null,
    },
    ...(inspection.schedule
      ? [
          {
            label: 'Schedule',
            value: (
              <Link to="/facilities/maintenance?tab=schedules" className="text-brand-700 hover:underline">
                {inspection.schedule.name}
              </Link>
            ),
          },
        ]
      : []),
    { label: 'Template', value: inspection.template?.name ?? null },
    { label: 'Started', value: inspection.startedAt ? formatDateTime(inspection.startedAt) : null },
    ...(inspection.completedAt
      ? [{ label: 'Completed', value: `${formatDateTime(inspection.completedAt)}${inspection.completedByName ? ` by ${inspection.completedByName}` : ''}` }]
      : []),
    { label: 'Notes', value: inspection.notes ? <span className="whitespace-pre-wrap">{inspection.notes}</span> : null },
  ];

  const completeButton = (size: 'sm' | 'md') => (
    <Button variant="primary" size={size} icon={<ClipboardCheck />} className="print:hidden" onClick={() => setCompleting(true)}>
      Complete inspection
    </Button>
  );

  return (
    <>
      <PageHeader
        back={{ to: '/facilities/inspections', label: 'Inspections' }}
        title={inspection.name}
        description={where || undefined}
        meta={
          <>
            <InspectionStatusBadge status={inspection.status} overdue={inspection.overdue} />
            <Badge>{INSPECTION_KIND_LABEL[inspection.kind]}</Badge>
            {completed && <ScoreBadge score={inspection.score} />}
          </>
        }
        actions={
          <div className="flex flex-wrap items-center gap-2 print:hidden">
            <Button icon={<Printer />} onClick={() => window.print()}>
              Print
            </Button>
            {permissions.edit && (
              <Button icon={<Pencil />} onClick={() => setEditing(true)}>
                Edit details
              </Button>
            )}
            {permissions.edit && (
              <Button icon={<Ban />} onClick={() => setCanceling(true)}>
                Cancel inspection
              </Button>
            )}
            {permissions.delete && (
              <Button variant="ghost" icon={<Trash2 />} onClick={() => setDeleting(true)}>
                Delete
              </Button>
            )}
          </div>
        }
      />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <CardHeader
              title="Checklist"
              description={
                completed
                  ? `Completed ${formatDateTime(inspection.completedAt)}${inspection.completedByName ? ` by ${inspection.completedByName}` : ''}`
                  : inspection.status === 'CANCELED'
                    ? 'This inspection was canceled.'
                    : permissions.respond
                      ? 'Results save as you mark them.'
                      : undefined
              }
              actions={permissions.respond && completeButton('sm')}
            />
            {inspection.items.length === 0 ? (
              <EmptyState icon={<ClipboardCheck />} title="This inspection has no items" />
            ) : (
              <>
                <ChecklistProgress items={inspection.items} />
                {missing.size > 0 && (
                  <div className="border-b border-slate-100 px-5 py-3 print:hidden">
                    <Alert tone="red">
                      {plural(missing.size, 'item')} still {missing.size === 1 ? 'needs' : 'need'} a result before the inspection can be completed.
                    </Alert>
                  </div>
                )}
                {groups.map((group, index) => (
                  <section key={`section-${group.section}`} className={cn(index > 0 && 'border-t border-slate-100')}>
                    {hasSections && (
                      <h3 className="flex items-center justify-between gap-3 border-b border-slate-100 bg-slate-50/80 px-5 py-2 text-xs font-semibold tracking-wide text-slate-500 uppercase">
                        <span>{group.section || 'General'}</span>
                        <span className="font-normal tracking-normal normal-case">
                          {group.items.filter((item) => item.result).length} of {group.items.length}
                        </span>
                      </h3>
                    )}
                    <ul className="divide-y divide-slate-100">
                      {group.items.map((item) => (
                        <ChecklistItem
                          key={item.id}
                          item={item}
                          canRespond={permissions.respond}
                          highlighted={missing.has(item.id)}
                          onSave={(response) => respond.mutate(response)}
                        />
                      ))}
                    </ul>
                  </section>
                ))}
                {permissions.respond && (
                  <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 px-5 py-3 print:hidden">
                    <p className="text-xs text-slate-500">{unanswered > 0 ? `${plural(unanswered, 'item')} left to mark` : 'Every item is marked'}</p>
                    {completeButton('md')}
                  </div>
                )}
              </>
            )}
          </Card>
        </div>

        <div className="space-y-6 print:order-first">
          <Card>
            <CardHeader title="Details" />
            <div className="px-5 py-4">
              <DescriptionList columns={1} items={details} />
            </div>
          </Card>

          <Card>
            <CardHeader title="Follow-up work orders" />
            {inspection.followUps.length === 0 ? (
              <p className="px-5 py-4 text-sm text-slate-500">
                {completed
                  ? 'None were opened.'
                  : followUpsOn
                    ? 'Each failed item opens a work order when the inspection is completed.'
                    : 'This template doesn’t open follow-up work orders.'}
              </p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {inspection.followUps.map((workOrder) => (
                  <li key={workOrder.id} className="px-5 py-3">
                    <div className="flex items-start justify-between gap-3">
                      <Link to={`/facilities/work-orders/${workOrder.id}`} className="min-w-0 text-sm font-medium text-slate-900 hover:text-brand-700">
                        <span className="text-slate-400">#{workOrder.number}</span> {workOrder.title}
                      </Link>
                      <WorkOrderStatusBadge status={workOrder.status} />
                    </div>
                    <p className="mt-0.5 text-xs text-slate-500">{workOrder.assignedTo ? `Assigned to ${workOrder.assignedTo.name}` : 'Unassigned'}</p>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card className="print:hidden">
            <CardHeader
              title="Documents"
              actions={
                desk.can('TECHNICIAN') && (
                  <Button size="sm" icon={<Upload />} onClick={() => setUploading(true)}>
                    Upload report
                  </Button>
                )
              }
            />
            <DocumentList documents={inspection.documents} emptyText="No reports uploaded yet" />
          </Card>
        </div>
      </div>

      <EditInspectionModal inspection={inspection} open={editing} onClose={() => setEditing(false)} />
      <CompleteInspectionModal
        inspection={inspection}
        open={completing}
        onClose={() => setCompleting(false)}
        onMissing={(itemIds) => {
          setMissing(new Set(itemIds));
          setScrollTarget(itemIds[0]);
        }}
      />
      <ConfirmDialog
        open={canceling}
        onClose={() => setCanceling(false)}
        onConfirm={() => cancel.mutate()}
        loading={cancel.isPending}
        title="Cancel this inspection?"
        description="It stays on record as canceled, with the results marked so far. It isn’t scored, and no follow-up work orders are opened."
        confirmLabel="Cancel inspection"
      />
      <ConfirmDialog
        open={deleting}
        onClose={() => setDeleting(false)}
        onConfirm={() => remove.mutate()}
        loading={remove.isPending}
        title="Delete this inspection?"
        description="The inspection and its results are deleted. This can’t be undone."
        confirmLabel="Delete inspection"
      />
      <DocumentUploadModal
        open={uploading}
        onClose={() => setUploading(false)}
        defaults={{ inspectionId: id, kind: 'INSPECTION_REPORT', title: `${inspection.name} report` }}
        onUploaded={() => void queryClient.invalidateQueries({ queryKey: ['facilities'] })}
      />
    </>
  );
}
